import { FC, useEffect, useState } from 'react';
import { GUsersTableProps } from '../items/interfaces/IUsers';
import { EditButton, DeleteButton } from '../utils/buttons';
import { Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Tooltip, IconButton } from '@mui/material';
import { useHUser } from '../items/hooks/HUsers';
import { useDocs } from '../items/hooks/HDocs';
import ConfirmForm from '../dialogs/ConfirmForm';
import { CheckBox, Cancel, FolderOpen as FolderOpenIcon } from '@mui/icons-material';
import UserDocsForm from '../dialogs/UserDocsForm';

/**
 * GUsersTable
 * Renders a table of user objects with edit controls.
 * @component
 * @param {GUsersTableProps} props - Props containing users data and edit handlers.
 * @returns {JSX.Element} Table of users with edit controls.
 */
const GUsersTable: FC<GUsersTableProps> = ({
    users,
    editRow,
    editValues,
    setEditRow,
    setEditValues,
    handleEditSave,
    handleDelete,
    onEditUser,
}) => {
    const { roles, loading: rolesLoading, fetchRoles, updateUserPassword } = useHUser();
    const { uploadUserDocumentToAzure } = useDocs();
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; user: any | null }>({
        open: false,
        user: null,
    });
    const [passwordDialog, setPasswordDialog] = useState<{ open: boolean; user: any | null }>({
        open: false,
        user: null,
    });
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [updatingPassword, setUpdatingPassword] = useState(false);
    const [docsDialogOpen, setDocsDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<string | number | null>(null);
    const [selectedUserName, setSelectedUserName] = useState<string | null>(null);
    const authTokenStr = localStorage.getItem('AUTH_TOKEN');
    const authUser = authTokenStr ? JSON.parse(authTokenStr) : null;
    const isAdmin = Number(authUser?.role_id ?? authUser?.roleId) === 1;

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const hasTwoFactorEnabled = (user: any) => {
        const secret = user?.totp_secret;
        return typeof secret === 'string' ? secret.trim().length > 0 : Boolean(secret);
    };

    const hasNdaEnabled = (user: any) => {
        // NDA may be boolean, string or numeric in different backends; coerce to boolean
        const v = user?.nda;
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string') return v === 'true' || v === '1';
        if (typeof v === 'number') return v === 1;
        return Boolean(v);
    };

    const getFullName = (user: any) => {
        const first = (user.first_name || '').trim();
        const last = (user.last_name || '').trim();
        const full = `${first} ${last}`.trim();
        return full || user.display_name || user.user || '-';
    };

    const getEmail = (user: any) => user.email || user.user || '-';

    const getRoleName = (user: any) => user.role_name || user.role || '-';

    const handleViewDocs = (userId: string | number, userName?: string | null) => {
        setSelectedUserId(userId);
        setSelectedUserName(userName ?? null);
        setDocsDialogOpen(true);
    };

    const handleCloseDocsDialog = () => {
        setDocsDialogOpen(false);
        setSelectedUserId(null);
    };

    const openPasswordDialog = (user: any) => {
        setPasswordDialog({ open: true, user });
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
    };

    const closePasswordDialog = () => {
        setPasswordDialog({ open: false, user: null });
        setNewPassword('');
        setConfirmPassword('');
        setPasswordError('');
    };

    const handleUpdatePassword = async () => {
        const userId = passwordDialog.user?.id;

        if (!userId) {
            setPasswordError('Invalid user selected.');
            return;
        }

        if (!newPassword.trim()) {
            setPasswordError('New password is required.');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('Password confirmation does not match.');
            return;
        }

        setUpdatingPassword(true);
        setPasswordError('');

        try {
            await updateUserPassword({ user_id: userId, password: newPassword });

            closePasswordDialog();
        } catch (error: any) {
            console.error('Failed to update user password:', error);
            setPasswordError(error?.message || 'Failed to update password.');
        } finally {
            setUpdatingPassword(false);
        }
    };

    const handleDeleteUser = async () => {
        const user = deleteConfirm.user;
        if (!user || !user.id) return;

        try {
            const API_BASE = process.env.REACT_APP_ENV === 'local'
                ? 'http://localhost:8090'
                : process.env.REACT_APP_API_BASE || '';

            const url = `${API_BASE}/api/users/delete-user-by-id`;
            const requestBody = { user_id: user.id };

            console.log('[GUsersTable] Deleting user', {
                userId: user.id,
                environment: process.env.REACT_APP_ENV,
                API_BASE,
                url,
                requestBody,
            });

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await response.json().catch(() => ({}));
            console.log('[GUsersTable] Delete response', { status: response.status, statusText: response.statusText, ok: response.ok, result });

            if (!response.ok || !result?.success) {
                const errMsg = result?.message || response.statusText || 'Failed to delete user';
                console.error('[GUsersTable] Delete error response:', errMsg, result);
                throw new Error(errMsg);
            }

            // Call the handleDelete prop if provided for additional handling (await it)
            if (handleDelete) {
                try {
                    await handleDelete({
                        ...user,
                        email: user.email,
                        role_name: getRoleName(user),
                        role: getRoleName(user),
                        desks: user.desks,
                    });
                } catch (err) {
                    console.error('[GUsersTable] handleDelete callback failed', err);
                }
            }

            console.log('[GUsersTable] User deleted successfully');
            setEditRow(null);
        } catch (error: any) {
            console.error('[GUsersTable] Failed to delete user:', error);
            alert(error?.message || 'Failed to delete user.');
        } finally {
            setDeleteConfirm({ open: false, user: null });
        }
    };

    return (
        <>
            <table className='elegant-table users-rounded-table'>
                <thead>
                    <tr>
                        <th>Full Name</th>
                        <th>Email</th>
                        <th>Role Name</th>
                        <th>2FA</th>
                        <th>NDA</th>
                        <th>Documents</th>
                        <th style={{ width: '220px' }}>Edit</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user, idx) => (
                        <tr key={idx}>
                            {editRow === idx ? (
                                <>
                                    <td>{getFullName(user)}</td>
                                    <td>{getEmail(user)}</td>
                                    <td>
                                        <Select
                                            value={editValues.role || ''}
                                            onChange={(e) => setEditValues({ ...editValues, role: e.target.value })}
                                            size='small'
                                            displayEmpty
                                            sx={{ minWidth: 100 }}
                                            disabled={rolesLoading}
                                        >
                                            {roles.map((roleObj) => (
                                                <MenuItem key={roleObj.id} value={roleObj.role}>
                                                    {roleObj.role}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </td>
                                    <td>
                                     {hasTwoFactorEnabled(user) ? <CheckBox style={{ color: 'green' }} /> : <Cancel style={{ color: 'red' }} />}
                                    </td>
                                    <td>
                                        <Tooltip title='Documents'>
                                            <IconButton
                                                size='small'
                                                color='primary'
                                            onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleViewDocs(user.id, getFullName(user));
                                                }}
                                            >
                                                <FolderOpenIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                    <td>
                                        <button
                                            className='icon-blue'
                                            onClick={() =>
                                                handleEditSave({
                                                    ...user,
                                                    email: user.email,
                                                    role_name: editValues.role ?? getRoleName(user),
                                                    role: editValues.role ?? getRoleName(user),
                                                    desks: user.desks,
                                                })
                                            }
                                        >
                                            Save
                                        </button>
                                        <button className='icon-blue' onClick={() => setEditRow(null)}>
                                            Cancel
                                        </button>
                                        {handleDelete && (
                                            <button
                                                className='icon-red'
                                                onClick={() => setDeleteConfirm({ open: true, user })}
                                            >
                                                Delete
                                            </button>
                                        )}
                                    </td>
                                </>
                            ) : (
                                <>
                                    <td>{getFullName(user)}</td>
                                    <td>{getEmail(user)}</td>
                                    <td>{getRoleName(user)}</td>
                                    <td>
                                        {hasTwoFactorEnabled(user) ? <CheckBox style={{ color: 'green' }} /> : <Cancel style={{ color: 'red' }} />}
                                    </td>
                                    <td>
                                     {hasNdaEnabled(user) ? <CheckBox style={{ color: 'green' }} /> : <Cancel style={{ color: 'red' }} />}
                                    </td>
                                    <td>
                                        <Tooltip title='Documents'>
                                            <IconButton
                                                size='small'
                                                color='primary'
                                                onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleViewDocs(user.id, getFullName(user));
                                                    }}
                                            >
                                                <FolderOpenIcon fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                    </td>
                                    <td style={{ width: '220px' }}>
                                        <EditButton
                                            onClick={() => {
                                                if (onEditUser) {
                                                    onEditUser(user);
                                                    return;
                                                }

                                                setEditRow(idx);
                                                setEditValues({ role: getRoleName(user) });
                                            }}
                                        />
                                        {handleDelete && (
                                            <DeleteButton onClick={() => setDeleteConfirm({ open: true, user })} />
                                        )}
                                        <Button className='icon-blue' onClick={() => openPasswordDialog(user)}>
                                            PWD
                                        </Button>

                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            <ConfirmForm
                open={deleteConfirm.open}
                title='Confirm Deletion'
                content={`Are you sure you want to delete user ${getFullName(deleteConfirm.user || {})}?`}
                onCancel={() => setDeleteConfirm({ open: false, user: null })}
                onConfirm={handleDeleteUser}
            />
            <Dialog open={passwordDialog.open} onClose={closePasswordDialog} fullWidth maxWidth='sm'>
                <DialogTitle>{getFullName(passwordDialog.user || {})}</DialogTitle>
                <DialogContent>
                    <TextField
                        fullWidth
                        margin='normal'
                        label='New Password'
                        type='password'
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                    <TextField
                        fullWidth
                        margin='normal'
                        label='Confirm Password'
                        type='password'
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        error={Boolean(passwordError)}
                        helperText={passwordError || ' '}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closePasswordDialog} disabled={updatingPassword}>Cancel</Button>
                    <Button onClick={handleUpdatePassword} variant='contained' disabled={updatingPassword}>
                        Update Password
                    </Button>
                </DialogActions>
            </Dialog>
            <UserDocsForm
                open={docsDialogOpen}
                onClose={handleCloseDocsDialog}
                userId={selectedUserId}
                userName={selectedUserName}
                canUpload={isAdmin}
                onFileDrop={async (file, userId) => {
                    console.log('[GUsersTable] onFileDrop triggered', {
                        userId,
                        fileName: file.name,
                        fileSize: file.size,
                        fileType: file.type,
                    });

                    const pickedFile = {
                        file,
                        name: file.name,
                        path: file.name,
                        bytes: await file.arrayBuffer(),
                    };
                    const success = await uploadUserDocumentToAzure(pickedFile, userId);

                    if (!success) {
                        throw new Error('Failed to upload user document');
                    }

                    console.log('[GUsersTable] uploadUserDocumentToAzure finished', {
                        userId,
                        fileName: file.name,
                    });
                }}
            />
        </>
    );
};

export default GUsersTable;
